import { useState, useCallback } from "react";
import useUsers from "hooks/userManagement/useUsers";
import { useAuth } from "contexts/AuthContext";
import apiClient from "utils/apiClient";

import { PROFILE_SERVICE_BASE_URL, STORAGE_BASE_URL } from "../constants";

const PROFILE_BASE_URL = PROFILE_SERVICE_BASE_URL;

const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic"];
const FILE_IDS_NOT_FOUND_PATTERN = /files?\s+with\s+fileids?.*not found/i;
const FILE_NOT_READY_MESSAGE =
  "Some uploaded files are not available yet. Please wait a few seconds and try again.";
const DEFAULT_ERROR_MESSAGE =
  "Something went wrong while processing the file request.";

function isImage(filename = "") {
  const lower = filename.toLowerCase();
  return IMAGE_EXTS.some((ext) => lower.endsWith(ext));
}

function guessContentType(filename = "") {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".heic")) return "image/heic";
  return "image/jpeg";
}

function isUsableDownloadUrl(value) {
  try {
    const url = new URL(value);
    const queryKeys = [...url.searchParams.keys()].map((key) => key.toLowerCase());
    const signedHeaders = url.searchParams.get("X-Goog-SignedHeaders");
    return (
      ["http:", "https:"].includes(url.protocol) &&
      !queryKeys.includes("ifgenerationmatch") &&
      (!signedHeaders || signedHeaders.toLowerCase() === "host")
    );
  } catch {
    return false;
  }
}

function getApiErrorMessage(err) {
  const responseMessage = err?.response?.data?.message;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage.trim();
  }
  if (typeof err?.message === "string" && err.message.trim()) {
    return err.message.trim();
  }
  return DEFAULT_ERROR_MESSAGE;
}

function isMissingFileIdsError(err) {
  const status = err?.response?.status ?? err?.response?.data?.code;
  const message = err?.response?.data?.message;
  return (
    Number(status) === 404 &&
    typeof message === "string" &&
    FILE_IDS_NOT_FOUND_PATTERN.test(message)
  );
}

function toReadableUploadError(err, fallbackMessage = DEFAULT_ERROR_MESSAGE) {
  const resolvedMessage = isMissingFileIdsError(err)
    ? FILE_NOT_READY_MESSAGE
    : getApiErrorMessage(err) || fallbackMessage;

  const readableError = new Error(resolvedMessage);
  readableError.status = err?.response?.status ?? err?.response?.data?.code;
  readableError.apiMessage = err?.response?.data?.message;
  readableError.cause = err;
  return readableError;
}

function normalizeWorkAuthorizationPayload(
  workAuthorizationInput,
  idDocumentIdsInput
) {
  const isObjectPayload =
    workAuthorizationInput &&
    typeof workAuthorizationInput === "object" &&
    !Array.isArray(workAuthorizationInput);

  const payload = isObjectPayload
    ? { ...workAuthorizationInput }
    : {
        workAuthorizationType: workAuthorizationInput,
        type: workAuthorizationInput,
      };

  if (Array.isArray(idDocumentIdsInput)) {
    payload.idDocumentIds = idDocumentIdsInput;
  }

  const normalizedType = payload.type || payload.workAuthorizationType || "";
  const normalizedCapability =
    typeof payload.capability === "string"
      ? payload.capability.trim().toUpperCase()
      : payload.capability;

  return {
    ...payload,
    type: normalizedType,
    workAuthorizationType: payload.workAuthorizationType || normalizedType,
    capability: normalizedCapability,
    notes:
      typeof payload.notes === "string" && payload.notes.trim()
        ? payload.notes.trim()
        : payload.notes || null,
  };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function withMissingFileRetry(fn, options = {}) {
  const { retries = 2, delayMs = 1000 } = options;
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      const shouldRetry = isMissingFileIdsError(err) && attempt < retries;
      if (!shouldRetry) throw err;
      await sleep(delayMs * (attempt + 1));
    }
  }

  throw lastError;
}

async function compressImageToTarget(file, opts = {}) {
  const {
    maxBytes = 500_000,
    maxWidth = 1920,
    maxHeight = 1920,
    initialQuality = 0.8,
    minQuality = 0.5,
    stepQuality = 0.1,
    scaleStep = 0.9,
    forceJpeg = true,
  } = opts;

  const getBlob = (canvas, quality) =>
    new Promise((resolve) => {
      canvas.toBlob(resolve, forceJpeg ? "image/jpeg" : file.type, quality);
    });

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      let width = img.width;
      let height = img.height;
      const aspect = width / height;

      if (width > maxWidth || height > maxHeight) {
        if (width > height) {
          width = maxWidth;
          height = Math.floor(width / aspect);
        } else {
          height = maxHeight;
          width = Math.floor(height * aspect);
        }
      }

      let quality = initialQuality;

      const draw = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        return canvas;
      };

      let canvas = draw();
      let blob = await getBlob(canvas, quality);
      let size = blob.size;

      while (
        size > maxBytes &&
        (quality > minQuality || width > 640 || height > 640)
      ) {
        if (quality > minQuality) {
          quality = Math.max(minQuality, quality - stepQuality);
        } else {
          width = Math.floor(width * scaleStep);
          height = Math.floor(height * scaleStep);
        }
        canvas = draw();
        blob = await getBlob(canvas, quality);
        size = blob.size;
      }

      if (size > file.size) {
        resolve({ blob: file, size: file.size, contentType: file.type });
      } else {
        resolve({
          blob,
          size,
          contentType: forceJpeg ? "image/jpeg" : file.type,
        });
      }
    };

    img.onerror = (e) => reject(e);
    img.src = URL.createObjectURL(file);
  });
}

export function useFileUpload() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [meta, setMeta] = useState(null);
  const { getUserById } = useUsers(0, 20, null, true, { autoFetch: false });
  const { user } = useAuth();

  const token = sessionStorage.getItem("authToken");

  const createUploadIntent = useCallback(
    async (
      userId,
      filename,
      fileSize,
      purpose = "USER_PROFILE",
      fileType = "PHOTO"
    ) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (userId) {
        const user = await getUserById(userId);
        if (!user || !user.id) throw new Error("User not found for uploading");
      }

      const body = { filename, fileType, size: fileSize, purpose };
      if (userId) body.userId = userId;

      const response = await apiClient.post(
        `${STORAGE_BASE_URL}/api/v1/files/upload-intent`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "O-CLIENT-NAME": "OMC",
          },
        }
      );

      return response.data;
    },
    [token, getUserById]
  );

  const uploadToPresignedUrl = useCallback(
    async (blob, uploadUrl, contentType, onProgress) => {
      if (onProgress) return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader("Content-Type", contentType || "application/octet-stream");
        xhr.timeout = 120000;
        xhr.upload.onprogress = event => { if (event.lengthComputable) onProgress(Math.round(event.loaded * 100 / event.total)); };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve(true) : reject(new Error("Document upload failed. Please retry."));
        xhr.onerror = xhr.ontimeout = () => reject(new Error("Document upload failed. Check your connection and retry."));
        xhr.send(blob);
      });
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType || "application/octet-stream" },
        body: blob,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Presigned upload failed: ${res.status} ${text}`);
      }
      return true;
    },
    []
  );

  const getFileMetadata = useCallback(
    async (fileId) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!fileId) throw new Error("fileId is required");
      try {
        const res = await apiClient.get(
          `${STORAGE_BASE_URL}/api/v1/files/${fileId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "O-CLIENT-NAME": "OMC",
            },
          }
        );
        return res.data;
      } catch (err) {
        console.warn(
          "[useFileUpload] getFileMetadata failed for",
          fileId,
          err?.response?.status
        );
        throw toReadableUploadError(err, "Failed to fetch file metadata.");
      }
    },
    [token]
  );

  const submitIdentityVerification = useCallback(
    async (userId, identificationDocumentType, idDocumentIds) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!userId) throw new Error("userId is required");
      if (!identificationDocumentType)
        throw new Error("identificationDocumentType is required");
      if (
        !idDocumentIds ||
        !Array.isArray(idDocumentIds) ||
        idDocumentIds.length === 0
      )
        throw new Error("idDocumentIds must be a non-empty array");

      const body = {
        idDocumentIds,
        identificationDocumentType,
      };

      try {
        const res = await withMissingFileRetry(() =>
          apiClient.post(
            `${PROFILE_BASE_URL}/api/v1/user-profiles/identity-verification/user/${userId}`,
            body,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "O-CLIENT-NAME": "OMC",
                "actor-id": user?.id || "",
              },
            }
          )
        );
        return res.data;
      } catch (err) {
        console.error("[useFileUpload] submitIdentityVerification failed", err);
        throw toReadableUploadError(
          err,
          "Failed to submit identity verification documents."
        );
      }
    },
    [token, user?.id]
  );

  const submitWorkAuthorization = useCallback(
    async (userId, workAuthorizationData, idDocumentIds) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!userId) throw new Error("userId is required");
      const body = normalizeWorkAuthorizationPayload(
        workAuthorizationData,
        idDocumentIds
      );
      if (!body.type && !body.workAuthorizationType)
        throw new Error("workAuthorizationType is required");
      if (
        !body.idDocumentIds ||
        !Array.isArray(body.idDocumentIds) ||
        body.idDocumentIds.length === 0
      )
        throw new Error("idDocumentIds must be a non-empty array");

      try {
        const res = await withMissingFileRetry(() =>
          apiClient.post(
            `${PROFILE_BASE_URL}/api/v1/user-profiles/work-authorization/user/${userId}`,
            body,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "O-CLIENT-NAME": "OMC",
                "actor-id": user?.id || "",
              },
            }
          )
        );
        return res.data;
      } catch (err) {
        console.error("[useFileUpload] submitWorkAuthorization failed", err);
        throw toReadableUploadError(
          err,
          "Failed to submit work authorization documents."
        );
      }
    },
    [token, user?.id]
  );

  const uploadFile = useCallback(
    async (userId, file, options = {}) => {

      setUploading(true);
      setError(null);

      try {

        let uploadBlob = file;
        let contentType = file.type || guessContentType(file.name);
        let fileSize = file.size;

        if (isImage(file.name) && !options.preserveOriginal) {
          const compressed = await compressImageToTarget(file, {
            forceJpeg: false,
          });
          uploadBlob = compressed.blob;
          fileSize = compressed.size;
          contentType = compressed.contentType;
        }

        const intent = await createUploadIntent(
          userId,
          file.name,
          fileSize,
          options.purpose,
          options.fileType
        );

        if (!intent.uploadUrl) {
          throw new Error("No uploadUrl in upload intent response");
        }

        if (!intent.file?.id) {
          throw new Error("No file.id in upload intent response");
        }

        await uploadToPresignedUrl(
          uploadBlob,
          intent.uploadUrl,
          intent.contentType || contentType,
          options.onUploadProgress
        );

        let downloadUrl = isUsableDownloadUrl(intent.downloadUrl)
          ? intent.downloadUrl
          : "";
        if (!downloadUrl) {
          try {
            const signedView = await apiClient.get(
              `${STORAGE_BASE_URL}/api/v1/files/photo/signed-url`,
              {
                params: { fid: intent.file.id },
                headers: {
                  Authorization: `Bearer ${token}`,
                  "O-CLIENT-NAME": "OMC",
                },
              }
            );
            const candidate =
              typeof signedView.data === "string"
                ? signedView.data
                : signedView.data?.url;
            if (isUsableDownloadUrl(candidate)) downloadUrl = candidate;
          } catch (err) {
            console.warn(
              "[useFileUpload] Could not refresh the document download URL",
              err?.response?.status || err?.message
            );
          }
        }

        // Improved meta fallback
        const filenameFromFile = intent.file?.filename;
        const metaToSet = filenameFromFile
          ? { filename: filenameFromFile }
          : { filename: file.name };

        setFileId(intent.file.id);
        setMeta(metaToSet);

        const returnValue = {
          fileId: intent.file.id,
          meta: metaToSet,
          file: intent.file || null,
          downloadUrl,
        };
        return returnValue;
      } catch (err) {
        const readableError = toReadableUploadError(
          err,
          "Failed to upload file."
        );
        setError(readableError.message);
        throw readableError;
      } finally {
        setUploading(false);
      }
    },
    [createUploadIntent, uploadToPresignedUrl, token]
  );

  return {
    uploadFile,
    uploading,
    error,
    fileId,
    meta,
    getFileMetadata,
    submitIdentityVerification,
    submitWorkAuthorization,
  };
}
